import AzureAD from './azure';

function install(Vue, config) {
    var azure = new AzureAD(config);
    let tokenReady = false;
    let currentToken = null;

    // ✅ Listen for MSAL redirect completion and set token immediately
    const setupLoginListener = () => {
        // Check for login completion on every page load
        const checkForLoginCompletion = async () => {
            console.log('🔍 Checking for MSAL login completion...');

            try {
                // MSAL v4: Warte auf Initialisierung!
                await azure.initializationPromise;
                const result = await azure.msal.handleRedirectPromise();
                if (result && result.accessToken) {
                    console.log('🎉 MSAL redirect login completed - setting token immediately!');
                    currentToken = result.accessToken;
                    Vue.http.headers.common['Authorization'] = 'Bearer ' + result.accessToken;
                    tokenReady = true;
                    console.log('✅ Token set from MSAL redirect completion');

                    // Force router to refresh to show authenticated state
                    if (window.app && window.app.$router) {
                        window.app.$forceUpdate();
                    }
                } else {
                    console.log('ℹ️ No login redirect result');
                }
            } catch (error) {
                console.log('⚠️ Error handling redirect promise:', error);
            }
        };

        // Check immediately
        Vue.nextTick(checkForLoginCompletion);

        // Also check after a short delay for router redirects
        setTimeout(checkForLoginCompletion, 100);
        setTimeout(checkForLoginCompletion, 500);
    };

    // Setup login listener
    setupLoginListener();

    // Check if already logged in
    Vue.nextTick(async () => {
        // MSAL v4: Warte auf Initialisierung!
        await azure.initializationPromise;
        const accounts = azure.msal.getAllAccounts();
        if (accounts.length > 0 && !currentToken) {
            console.log('🔍 User already logged in - trying to get cached token');
            acquireToken();
        }
    });

    // ✅ Simple token acquisition for already logged in users
    const acquireToken = async () => {
        // MSAL v4: Warte auf Initialisierung!
        await azure.initializationPromise;
        const accounts = azure.msal.getAllAccounts();
        if (accounts.length === 0) {
            console.log('👤 No accounts - no token needed');
            return null;
        }

        const tokenRequest = {
            scopes: ["https://graph.microsoft.com/.default"],
            account: accounts[0]
        };

        try {
            console.log('🔄 Getting token for already logged in user...');
            const response = await azure.msal.acquireTokenSilent(tokenRequest);
            currentToken = response.accessToken;
            Vue.http.headers.common['Authorization'] = 'Bearer ' + response.accessToken;
            tokenReady = true;
            console.log('✅ Token acquired for existing login');
            return response.accessToken;
        } catch (error) {
            console.log('❌ Token acquisition failed for existing user:', error);
            return null;
        }
    };

    // ✅ SIMPLE API Interceptor - Use existing token when available
    Vue.http.interceptors.push(function (request, next) {
        console.log('🚀 INTERCEPTOR CALLED for:', request.url);

        // Always allow auth-related requests
        const isAuthRequest = request.url.includes('login.microsoftonline.com') ||
            request.url.includes('/oauth2/') ||
            request.url.includes('/common/') ||
            request.url.includes('.well-known');

        if (isAuthRequest) {
            console.log('🔓 Allowing authentication request');
            next();
            return;
        }

        // Only handle API requests
        // Prüfe, ob es ein API-Request ist (entweder vollständige URL oder relativer Path)
        const apiEndpoints = ['search', 'klasse', 'testdefinition', 'standort', 'gegenstand',
            'funktionstest', 'lieferschein', 'papierkorb', 'verfahren',
            'modell', 'hersteller', 'gegenstandaktion'];
        const isApiRequest = request.url.includes('/api/') ||
            apiEndpoints.some(endpoint => request.url.includes(endpoint));
        console.log('🔍 Is API request?', isApiRequest, 'for URL:', request.url);
        if (!isApiRequest) {
            next();
            return;
        }

        // Check if user is logged in
        const accounts = azure.msal.getAllAccounts();
        if (accounts.length === 0) {
            console.log('👤 No user - allowing unauthenticated API request');
            next();
            return;
        }

        // User is logged in - check if we have a token
        console.log('🔍 Interceptor check - currentToken:', currentToken ? 'EXISTS' : 'NULL');
        if (currentToken) {
            console.log('✅ Using existing token for API request');
            request.headers.set('Authorization', 'Bearer ' + currentToken);
            next();
            return;
        }

        // No token yet - try to get one
        console.log('🔄 No token available - acquiring for API request');
        acquireToken().then(token => {
            if (token) {
                request.headers.set('Authorization', 'Bearer ' + token);
                next();
            } else {
                console.log('🚫 Could not acquire token');
                next();  // Let it through - server will respond with 401 if needed
            }
        }).catch(error => {
            console.log('🚫 Token acquisition error:', error);
            next();  // Let it through - server will respond with 401 if needed
        });
    });

    // ✅ PRODUCTION-READY 401 Response Handler
    Vue.http.interceptors.push(function (request, next) {
        next(function (res) {
            if (res.status === 401) {
                console.log('🔐 401 received - attempting token refresh...');

                if (!azure || !azure.msal) {
                    console.log('❌ Azure MSAL instance not available for refresh');
                    return;
                }

                const accounts = azure.msal.getAllAccounts();
                if (accounts.length > 0) {
                    const tokenRequest = {
                        scopes: ["https://graph.microsoft.com/.default"],
                        account: accounts[0],
                        forceRefresh: true
                    };

                    console.log('🔄 Refresh token request with scopes:', tokenRequest.scopes);

                    azure.msal.acquireTokenSilent(tokenRequest)
                        .then(response => {
                            console.log('✅ Token refreshed successfully');
                            console.log('🔑 Updating global Authorization header...');

                            // Set as global header for all requests
                            Vue.http.headers.common['Authorization'] = 'Bearer ' + response.accessToken;

                            console.log('🔍 Global headers updated:', Vue.http.headers.common);

                            // 🔄 RETRY the original request with new token
                            console.log('🔄 Retrying original request with fresh token...');
                            const newRequest = Object.assign({}, request);
                            newRequest.headers.set('Authorization', 'Bearer ' + response.accessToken);

                            // Retry the request
                            Vue.http(newRequest).then(retryResponse => {
                                console.log('✅ Retry successful:', retryResponse.status);
                            }).catch(retryError => {
                                console.log('❌ Retry failed:', retryError);
                            });
                        })
                        .catch(error => {
                            console.log('❌ Token refresh failed:', error);
                            // Redirect to login after multiple failures
                            if (error.errorCode === 'interaction_required') {
                                azure.login();
                            }
                        });
                }
            }
        });
    });

    // ✅ Initialize global headers immediately and with quick retry
    const initializeHeaders = async () => {
        console.log('🔄 Checking for existing authentication...');
        // MSAL v4: Warte auf Initialisierung!
        await azure.initializationPromise;
        const accounts = azure.msal.getAllAccounts();
        console.log('📊 Found accounts:', accounts.length);

        if (accounts.length > 0) {
            console.log('👤 User account found, setting up initial headers...');
            const tokenRequest = {
                scopes: ["https://graph.microsoft.com/.default"],
                account: accounts[0]
            };

            azure.msal.acquireTokenSilent(tokenRequest)
                .then(response => {
                    console.log('✅ Initial token acquired');
                    Vue.http.headers.common['Authorization'] = 'Bearer ' + response.accessToken;
                    console.log('🔑 Global headers set on startup');

                    // Trigger router update to show authenticated state
                    if (window.app && window.app.$router) {
                        window.app.$forceUpdate();
                    }
                })
                .catch(error => {
                    console.log('⚠️ Initial token acquisition failed:', error);
                });
        } else {
            console.log('❌ No accounts found');
        }
    };

    // Try immediately
    Vue.nextTick(initializeHeaders);

    // Fallback after 200ms if first attempt was too early
    setTimeout(initializeHeaders, 200);

    Object.defineProperties(Vue.prototype, {
        $azure: {
            get() {
                return azure;
            }
        }
    });
}

export default install;
